import {Component, OnDestroy, OnInit} from '@angular/core';
import {UserSubRegionSelectionService} from './user-sub-region-selection.service';
import {FeatureToggleService} from '../../shared/feature-toggle/feature-toggle.service';
import {FeatureToggle} from '../../shared/feature-toggle/feature-toggle';
import {combineLatest, of, Subscription, switchMap} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';
import {PermissionService} from '../../core/permission.service';
import {FormBuilder, FormGroup} from '@angular/forms';
import {SubRegion} from '../../region-management/sub-region-management/sub-region.model';
import {DropdownChangeEvent} from 'primeng/dropdown';
import {USER_SUB_REGION_PERMISSIONS} from '../../region-management/sub-region-management/user-sub-region.permissions';

@Component({
    selector: 'app-user-sub-region-selection',
    templateUrl: './user-sub-region-selection.html',
    styleUrl: './user-sub-region-selection.scss'
})
export class UserSubRegionSelection implements OnInit, OnDestroy {

    subscriptions = new Subscription();
    addUserSubRegionEnabled = false;
    loading = true;
    userSubRegions: SubRegion[];
    userSubRegionSelectionForm: FormGroup;

    private readonly SHOW_ALL_DEVICES_OPTION: SubRegion = {
        id: null,
        name: 'Show All Devices',
        regionName: null,
    };

    constructor(
      private readonly userSubRegionSelectionService: UserSubRegionSelectionService,
      private readonly featureToggleService: FeatureToggleService,
      private readonly permissionService: PermissionService,
      private readonly formBuilder: FormBuilder,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.initObservables();
    }

    private readonly initObservables = () => {
        // Step 1: Check if feature and permission are enabled
        const canAddUserSubRegion$ = combineLatest([
            this.featureToggleService.isFeatureEnabled$(FeatureToggle.SUB_REGION_MANAGEMENT),
            this.permissionService.hasUserPermission$(USER_SUB_REGION_PERMISSIONS.ADD),
        ]).pipe(
          map(([subRegionEnabled, userSubRegionEnabled]) => subRegionEnabled && userSubRegionEnabled),
          tap(enabled => this.addUserSubRegionEnabled = enabled),
          filter(Boolean)
        );

        // Step 2: Get user sub-regions and update list
        const userSubRegion$ = canAddUserSubRegion$.pipe(
          switchMap(() => this.userSubRegionSelectionService.getUserSubRegions()),
          tap(userSubRegions => this.userSubRegions = [this.SHOW_ALL_DEVICES_OPTION, ...userSubRegions]),
          tap(() => this.loading = false)
        );

        // Step 3: Get user sub-region and initialize form
        this.subscriptions.add(
          userSubRegion$.pipe(
            switchMap(() => this.userSubRegionSelectionService.getUserSubRegion().pipe(
              catchError(() => of(null))
            )),
            tap(userSubRegion => this.userSubRegionSelectionForm
              .get('userSubRegionId')
              .patchValue(userSubRegion, { emitEvent: false })
            )
          ).subscribe()
        );
    }

    private readonly initForm = () => {
        this.userSubRegionSelectionForm = this.formBuilder.group({
            userSubRegionId: [null, []],
        });
    }

    userSubRegionChanged = (event: DropdownChangeEvent) => {
        const subRegionId = event.value;
        this.loading = true;
        this.subscriptions.add(
          this.userSubRegionSelectionService.setUserSubRegion(subRegionId)
            .pipe(tap(() => this.loading = false))
            .subscribe()
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
